import { buttonLabel, defaultBindings, keyLabel, type BindAction, type Bindings } from '../core/bindings';

export type GamepadFamily = 'playstation' | 'xbox' | 'generic';
export type DeviceKind = 'keyboard' | 'touch' | 'gamepad';

export interface ActiveDevice {
  kind: DeviceKind;
  family: GamepadFamily;
}

export interface InputPrompts {
  device: DeviceKind;
  confirm: string;
  back: string;
}

/** Best-effort browser Gamepad API family detection; unknown pads keep generic labels. */
export function gamepadFamily(id: string): GamepadFamily {
  const value = (id ?? '').toLowerCase();
  if (/playstation|dualshock|dualsense|sony|054c/.test(value)) return 'playstation';
  if (/xbox|xinput|microsoft|045e/.test(value)) return 'xbox';
  return 'generic';
}

export function inputPrompts(gamepadId: string | null, coarsePointer = false): InputPrompts {
  if (gamepadId) return promptsFor({ kind: 'gamepad', family: gamepadFamily(gamepadId) });
  return promptsFor({ kind: coarsePointer ? 'touch' : 'keyboard', family: 'generic' });
}

export function promptsFor(device: ActiveDevice): InputPrompts {
  if (device.kind === 'gamepad') {
    if (device.family === 'playstation') return { device: 'gamepad', confirm: 'Cross · Confirm', back: 'Circle · Back' };
    if (device.family === 'xbox') return { device: 'gamepad', confirm: 'A · Confirm', back: 'B · Back' };
    return { device: 'gamepad', confirm: 'Button 1 · Confirm', back: 'Button 2 · Back' };
  }
  if (device.kind === 'touch') return { device: 'touch', confirm: 'Tap · Confirm', back: 'Tap Back · Return' };
  return { device: 'keyboard', confirm: 'Enter · Confirm', back: 'Esc · Back' };
}

/* --------------------------------------------------------- active device */

/**
 * The device the player used most recently. Keyboard/mouse, touch and gamepad
 * input each switch it, and every prompt re-renders through `onDeviceChange`,
 * so hints follow whatever the player is actually holding.
 */
let active: ActiveDevice = {
  kind: typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches ? 'touch' : 'keyboard',
  family: 'generic',
};
let bindings: Bindings = defaultBindings();
const listeners = new Set<() => void>();

export function activeDevice(): ActiveDevice {
  return active;
}

let lastFamily: GamepadFamily = 'generic';

/** Family of the most recently used pad (for controller labels while the keyboard is active). */
export function lastPadFamily(): GamepadFamily {
  return lastFamily;
}

export function setActiveDevice(next: ActiveDevice): void {
  if (next.kind === 'gamepad') lastFamily = next.family;
  if (next.kind === active.kind && next.family === active.family) return;
  active = next;
  notify();
}

/** Bindings prompts should describe (Game sets this from the save). */
export function setPromptBindings(next: Bindings): void {
  bindings = next;
  notify();
}

export function promptBindings(): Bindings {
  return bindings;
}

export function onDeviceChange(fn: () => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function notify(): void {
  for (const fn of [...listeners]) fn();
}

const STICK_ACTIVITY = 0.5;
let heldButtons = new Set<number>();
let sampledPad: string | null = null;

/**
 * Called every frame with the first connected pad. A newly pressed button or
 * a stick pushed past half-way makes it active; buttons held since connect
 * (some pads report a stuck trigger) and non-stick axes never do.
 */
export function noteGamepad(pad: Gamepad | null): void {
  if (!pad) {
    heldButtons = new Set();
    sampledPad = null;
    if (active.kind === 'gamepad') setActiveDevice({ kind: defaultNonPadKind(), family: 'generic' });
    return;
  }
  const nowHeld = new Set<number>();
  pad.buttons.forEach((b, i) => { if (b.pressed) nowHeld.add(i); });
  const firstSample = sampledPad !== pad.id;
  sampledPad = pad.id;
  const newPress = !firstSample && [...nowHeld].some((i) => !heldButtons.has(i));
  heldButtons = nowHeld;
  const pushed = pad.axes.slice(0, 4).some((a) => Math.abs(a) > STICK_ACTIVITY) && pad.mapping === 'standard';
  if (newPress || pushed) setActiveDevice({ kind: 'gamepad', family: gamepadFamily(pad.id) });
}

function defaultNonPadKind(): DeviceKind {
  return typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches ? 'touch' : 'keyboard';
}

/** Wire keyboard/mouse/touch listeners that switch the active device. */
export function trackDeviceInput(target: Window = window): void {
  target.addEventListener('keydown', () => setActiveDevice({ kind: 'keyboard', family: 'generic' }), { capture: true, passive: true });
  target.addEventListener('pointerdown', (e) => setActiveDevice({ kind: e.pointerType === 'touch' || e.pointerType === 'pen' ? 'touch' : 'keyboard', family: 'generic' }), { capture: true, passive: true });
  target.addEventListener('wheel', () => setActiveDevice({ kind: 'keyboard', family: 'generic' }), { capture: true, passive: true });
}

export function currentInputPrompts(): InputPrompts {
  return promptsFor(active);
}

/* ----------------------------------------------------------------- glyphs */

export interface Glyph {
  text: string;
  /** CSS modifier: 'key', 'pad', 'pad-ps-cross', 'pad-xbox-a', 'touch', … */
  kind: string;
}

const PS_FACE = ['cross', 'circle', 'square', 'triangle'];
const XBOX_FACE = ['a', 'b', 'x', 'y'];

function padGlyph(button: number, family: GamepadFamily): Glyph {
  const text = buttonLabel(button, family);
  if (button <= 3) {
    if (family === 'playstation') return { text, kind: `pad pad-ps-${PS_FACE[button]}` };
    return { text, kind: `pad pad-xbox-${XBOX_FACE[button]}` };
  }
  return { text, kind: 'pad pad-shoulder' };
}

const TOUCH_LABEL: Partial<Record<BindAction | 'move', string>> = {
  move: 'Drag the stick to move',
  ability: 'Tap the ability button',
  pause: 'Tap pause',
  zoomIn: 'Pinch to zoom',
  zoomOut: 'Pinch to zoom',
};

/**
 * Glyphs for an action on a device, from the current bindings. 'move' is the
 * composite of the four directions (WASD / arrows / left stick).
 */
export function glyphsFor(action: BindAction | 'move', device: ActiveDevice = active, b: Bindings = bindings): Glyph[] {
  if (device.kind === 'touch') return [{ text: TOUCH_LABEL[action] ?? '', kind: 'touch' }];
  if (device.kind === 'gamepad') {
    if (action === 'move') return [{ text: device.family === 'playstation' ? 'L-stick' : 'LS', kind: 'pad pad-shoulder' }];
    return b.gamepad[action].slice(0, 1).map((button) => padGlyph(button, device.family));
  }
  if (action === 'move') {
    const first = (a: BindAction) => b.keyboard[a][0];
    const wasd = ['KeyW', 'KeyA', 'KeyS', 'KeyD'];
    const keys = [first('moveUp'), first('moveLeft'), first('moveDown'), first('moveRight')];
    if (keys.every((k, i) => k === wasd[i])) return [{ text: 'WASD', kind: 'key' }];
    return keys.filter(Boolean).map((k) => ({ text: keyLabel(k), kind: 'key' }));
  }
  return b.keyboard[action].slice(0, 1).map((code) => ({ text: keyLabel(code), kind: 'key' }));
}
